import React, { useState } from "react";
import { Box, Typography, useTheme, useMediaQuery, IconButton } from "@mui/material";
import { Star, ChevronLeft, ChevronRight } from "@mui/icons-material";
import { motion, AnimatePresence, Variants } from "motion/react";
import styled from "styled-components";
import { useThemeMode } from "@/colors";
import { useColors } from "@/hooks/useColors";
import { GradientText } from "../dashboard/Gradient";

const TestimonialsContainer = styled(Box)`
  margin-top: 48px;
  padding: 80px 24px;
  background: ${props => props.theme.palette.background.default};
  text-align: center;
`;

const ContentWrapper = styled(Box)`
  max-width: 1200px;
  margin: 0 auto;
`;

const RatingContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 48px;
`;

const RatingText = styled(Typography)`
  color: ${props => props.theme.palette.text.primary};
  font-size: 20px !important;
  font-weight: 700;
  margin-right: 16px;
`;

const StarsContainer = styled(Box)`
  display: flex;
  gap: 4px;
`;

const CarouselContainer = styled(Box)`
  .MuiPaper-root {
    background: transparent !important;
    box-shadow: none !important;
  }
  
  .CarouselItem {
    padding: 0 !important;
  }
`;

const CarouselWrapper = styled(Box)`
  position: relative;
  overflow: hidden;
  border-radius: 16px;
`;

const NavigationButton = styled(IconButton)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background: ${props => props.theme.palette.background.paper} !important;
  color: ${props => props.theme.palette.text.primary} !important;
  width: 48px;
  height: 48px;
  border: 1px solid ${props => props.theme.palette.divider};
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.palette.action.hover} !important;
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }
`;

const TestimonialGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  max-width: 1000px;
  margin: 0 auto;
  min-height: 300px;
  padding-top: 16px;
`;

const TestimonialCard = styled(motion.div)`
  background: ${props => props.theme.palette.background.paper};
  border-radius: 12px;
  padding: 16px;
  border: 1px solid ${props => props.theme.palette.divider};
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
  }
`;

const TestimonialText = styled(Typography)`
  color: ${props => props.theme.palette.text.secondary} !important;
  font-style: italic;
  line-height: 1.6;
  margin-bottom: 16px !important;
  text-align: left;
  padding: 18px;
  padding-top: 8px;
`;

// Using regular Typography with inline styles for better theme access
const AuthorText = styled(Typography)`
  font-weight: 500;
  text-align: right
`;

const DotsContainer = styled(Box)`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 32px;
`;

const Dot = styled(Box)<{ active: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.active ? props.theme.palette.primary.main : props.theme.palette.divider};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.palette.primary.main};
    transform: scale(1.2);
  }
`;

const testimonials = [
  [
    {
      text: "The idea is original and the final result is better than I imagined",
      author: "Angela S"
    },
    {
      text: "Created my map perfectly - it was fast and easy to use.",
      author: "Andrés C"
    },
    {
      text: "The customization options are great - much cheaper than other websites.",
      author: "Carla R"
    },
    {
      text: "Super intuitive and was very simple to use. Thank you!",
      author: "Sebastián F"
    },
    {
      text: "Great maps!",
      author: "Gabriela A"
    },
    {
      text: "Making the map was simple. I could change the colors and add my message without any problem.",
      author: "David M"
    }
  ],
  [
    {
      text: "Steps were very clear. I enjoyed designing it.",
      author: "Michael R"
    },
    {
      text: "The platform is very friendly. I liked that I could move the map and see what I was getting",
      author: "Sarah L"
    },
    {
      text: "Genius! Highly recommended!",
      author: "David K"
    },
    {
      text: "I liked that I could zoom and place the pin exactly where we met. It felt very personal",
      author: "Emma T"
    },
    {
      text: "Made a perfect gift for my partner 10/10",
      author: "Neil S"
    },
    {
      text: "It was very quick and enjoyable.",
      author: "Natalia Q"
    }
  ],
  [
    {
      text: "I was worried about language but everything was easy to understand. Very clear steps.",
      author: "Raúl E"
    },
    {
      text: "Easy to use interface and stunning results every time.",
      author: "Lisa M"
    },
    {
      text: "Perfect gift for any occasion. Everyone loves their custom map!",
      author: "Robert H"
    },
    {
      text: "Made it for a housewarming and they loved it!",
      author: "Maria G"
    },
    {
      text: "It felt nice to create something so personal by myself.",
      author: "Daniela A"
    },
    {
      text: "Every step felt clear. I didn't need help or tutorials to build it.",
      author: "Adriana T"
    }
  ]
];

export const TestimonialsCarousel: React.FC = () => {
  const theme = useTheme();
  const { colors: themeColors } = useThemeMode();
  const colors = useColors();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const slideVariants: Variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  const cardVariants: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: "easeOut"
      }
    })
  };
  const allTestimonials = testimonials[currentIndex];

  return (
    <TestimonialsContainer>
      <Typography variant="h2" mb="32px" mt="64px" color={theme.palette.mode === "dark" ? colors.text.brand.darkest : colors.text.neutral.dark}>
        Trusted by <GradientText variant="h2" display="inline">150k+ customers</GradientText> across the world
      </Typography>
      <ContentWrapper>
        <RatingContainer>
          <RatingText>4.8</RatingText>
          <StarsContainer>
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: '2rem'
                }}
              />
            ))}
          </StarsContainer>
        </RatingContainer>

        <CarouselContainer>
          <CarouselWrapper>
            <AnimatePresence mode="wait" custom={currentIndex}>
              <TestimonialGrid
                key={currentIndex}
                custom={currentIndex}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 }
                }}
              >
                {(isMobile ? allTestimonials.slice(0,4) : allTestimonials).map((testimonial, cardIndex) => (
                  <TestimonialCard
                    key={cardIndex}
                    custom={cardIndex}
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover={{ 
                      scale: 1.02,
                      transition: { duration: 0.3 }
                    }}
                  >
                    <TestimonialText variant="body1">
                      {testimonial.text}
                    </TestimonialText>
                    <AuthorText 
                      variant="body2" 
                      sx={{ color: colors.text.neutral.dark }}
                    >
                      – {testimonial.author}
                    </AuthorText>
                  </TestimonialCard>
                ))}
              </TestimonialGrid>
            </AnimatePresence>
            
            {!isMobile && (
              <>
                <NavigationButton 
                  className="left" 
                  onClick={prevSlide}
                  aria-label="Previous testimonials"
                >
                  <ChevronLeft />
                </NavigationButton>
                <NavigationButton 
                  className="right" 
                  onClick={nextSlide}
                  aria-label="Next testimonials"
                >
                  <ChevronRight />
                </NavigationButton>
              </>
            )}
          </CarouselWrapper>
          
          <DotsContainer>
            {testimonials.map((_, index) => (
              <Dot
                key={index}
                active={index === currentIndex}
                onClick={() => goToSlide(index)}
              />
            ))}
          </DotsContainer>
        </CarouselContainer>
      </ContentWrapper>
    </TestimonialsContainer>
  );
};
